import type { PlayerStatus } from './MediaPlayer.tsx';
import type { VisionStatus } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';

interface Props {
  status: PlayerStatus | null;
  /** null when this source has no counting pipeline at all. */
  vision: VisionStatus | null;
  sourceLabel: string;
  attribution: string | null;
  onRetry: () => void;
  onBack: () => void;
  onCalibrate: (() => void) | null;
}

/** Replaces the count panel while a video source is previewed: health, epochs and honest limits. */
export function VideoStatusPanel({ status, vision, sourceLabel, attribution, onRetry, onBack, onCalibrate }: Props) {
  const { t } = useI18n();
  const h = status?.health;
  return (
    <section className="video-status" aria-label={sourceLabel} data-testid="video-status" data-phase={status?.phase ?? 'loading'} data-health={h?.status ?? 'OFFLINE'} data-epoch-count={status?.epochCount ?? 0} data-epoch-reason={status?.epochReason ?? 'LOAD'} data-epoch-history={status?.epochHistory.join(',') ?? ''}>
      <div className="vs-row">
        <strong>{sourceLabel}</strong>
        <span className={`state-chip vs-${h?.status ?? 'OFFLINE'}`} role="status">
          {t(`video.status.${h?.status ?? 'OFFLINE'}` as MessageKey)}
        </span>
        <span className="chip">{t(`video.phase.${status?.phase ?? 'loading'}` as MessageKey)}</span>
        {status?.reconnecting && <span className="chip conn-reconnecting">{t('video.reconnecting')}</span>}
      </div>
      <dl className="vs-grid">
        <dt>FPS</dt>
        <dd data-testid="vs-fps">{status?.measuredFps ?? '—'}</dd>
        <dt>{t('video.epoch', { n: '' }).trim()}</dt>
        <dd>
          #{status?.epochCount ?? 0} · {status?.epochReason ?? 'LOAD'}
        </dd>
        <dt>Size</dt>
        <dd>{status && status.width > 0 ? `${status.width}×${status.height}` : '—'}</dd>
        <dt>Age</dt>
        <dd>{status?.mediaAgeMs != null ? t('video.age', { s: (status.mediaAgeMs / 1000).toFixed(1) }) : t('video.clockUnknown')}</dd>
      </dl>
      {h && h.reasons.length > 0 && <p className="note">{h.reasons.map((r) => t(`video.health.${r}` as MessageKey)).join(' · ')}</p>}
      {status?.error && (
        <div className="vs-error" role="alert" data-testid="video-error" data-error={status.error}>
          <p>{t(`video.error.${status.error}` as MessageKey)}</p>
          {status.errorDetail && <p className="note mono">{status.errorDetail}</p>}
          <button type="button" className="ghost-btn bordered" onClick={onRetry}>
            {t('video.retry')}
          </button>
        </div>
      )}
      {vision ? (
        <div className={`vision-box vision-${vision.state}`} data-testid="vision-status" data-state={vision.state}>
          <strong>{t(`vision.${vision.state}` as MessageKey)}</strong>
          {vision.modelVersion && <span className="note mono">{vision.modelVersion}</span>}
          {vision.lagMicros !== null && (
            <span className="note">{vision.lagMicros >= 0 ? t('vision.lag', { s: (vision.lagMicros / 1e6).toFixed(2) }) : t('vision.ahead', { s: (-vision.lagMicros / 1e6).toFixed(1) })}</span>
          )}
          {vision.configHash && <span className="note mono">cfg {vision.configHash.slice(0, 12)}</span>}
          {vision.pendingCalibration && <span className="note">{t('cal.pending')}</span>}
          {vision.message && <span className="note mono">{vision.message}</span>}
        </div>
      ) : (
        <p className="warn-box">{t('video.counterMissing')}</p>
      )}
      <div className="cal-row">
        {onCalibrate && (
          <button type="button" className="ghost-btn bordered" onClick={onCalibrate}>
            {t('cal.open')}
          </button>
        )}
      {attribution && <p className="note attribution">{attribution}</p>}
        <button type="button" className="ghost-btn bordered" onClick={onBack}>
          {t('video.backToSim')}
        </button>
      </div>
    </section>
  );
}
